import { lazy, Suspense, useEffect, useState } from "react";
import {
  CalendarCheck2,
  ClipboardCheck,
  FileUp,
  PackageCheck,
  Search,
  UserRound,
} from "lucide-react";
import { api } from "@/lib/api";
import { Input, Label, Select } from "@/components/ui/field";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Profile, ProfileActivity } from "@/shared/types";

const ProfileImportDialog = lazy(() =>
  import("./ProfileImportDialog").then((module) => ({
    default: module.ProfileImportDialog,
  })),
);

type ProfileRelationship = Profile["relationship"];
type ProfileCounts = Record<ProfileRelationship, number>;
const PAGE_SIZE = 50;

const relationshipLabel: Record<Profile["relationship"], string> = {
  neighbor: "Neighbor",
  volunteer: "Hope Army Volunteer",
  supporter: "Supporter",
  staff: "Hope Army Team",
};

const profileTabs: Array<{
  value: ProfileRelationship;
  label: string;
  searchLabel: string;
}> = [
  { value: "neighbor", label: "Neighbor", searchLabel: "neighbors" },
  {
    value: "volunteer",
    label: "Hope Army Volunteer",
    searchLabel: "Hope Army volunteers",
  },
  { value: "supporter", label: "Supporter", searchLabel: "supporters" },
  {
    value: "staff",
    label: "Hope Army Team",
    searchLabel: "Hope Army team members",
  },
];

const languageLabel = (profile: Profile) =>
  profile.preferred_locale.split("-")[0].toUpperCase();

export function ProfilesPage() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [relationship, setRelationship] =
    useState<ProfileRelationship>("neighbor");
  const [counts, setCounts] = useState<ProfileCounts>({
    neighbor: 0,
    volunteer: 0,
    supporter: 0,
    staff: 0,
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<Profile | null>(null);
  const [activity, setActivity] = useState<ProfileActivity | null>(null);

  const loadCounts = (value = search) => {
    const query = new URLSearchParams({ search: value });
    return api<ProfileCounts>(`/admin/profile-counts?${query}`).then(setCounts);
  };

  useEffect(() => {
    let active = true;
    const query = new URLSearchParams({
      relationship,
      search,
      limit: String(PAGE_SIZE),
      offset: String((page - 1) * PAGE_SIZE),
    });
    void api<Profile[]>(`/admin/profiles?${query}`).then((results) => {
      if (active) setProfiles(results);
    });
    return () => {
      active = false;
    };
  }, [relationship, search, page, refreshKey]);

  useEffect(() => {
    let active = true;
    const query = new URLSearchParams({ search });
    void api<ProfileCounts>(`/admin/profile-counts?${query}`).then(
      (results) => {
        if (active) setCounts(results);
      },
    );
    return () => {
      active = false;
    };
  }, [search, refreshKey]);

  const total = counts[relationship];
  const activeTab = profileTabs.find((tab) => tab.value === relationship)!;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstResult = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastResult = Math.min((page - 1) * PAGE_SIZE + profiles.length, total);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    if (!selected) {
      setActivity(null);
      return;
    }
    let active = true;
    setActivity(null);
    void api<ProfileActivity>(`/admin/profiles/${selected.id}/activity`).then(
      (result) => {
        if (active) setActivity(result);
      },
    );
    return () => {
      active = false;
    };
  }, [selected?.id]);

  const update = async (updates: Partial<Profile>) => {
    if (!selected) return;
    const profile = await api<Profile>(`/admin/profiles/${selected.id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
    if (profile.relationship === relationship) {
      setSelected(profile);
      setProfiles((all) =>
        all.map((item) => (item.id === profile.id ? profile : item)),
      );
    } else {
      setSelected(null);
      setProfiles((all) => all.filter((item) => item.id !== profile.id));
    }
    await loadCounts();
  };
  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profiles</h1>
          <p className="mt-2 text-muted-foreground">
            Keep neighbor households and the Hope Hangar Army organized in
            focused lists.
          </p>
        </div>
        <Button onClick={() => setImportOpen(true)}>
          <FileUp size={17} />
          Import profiles
        </Button>
      </div>
      <div
        className="mt-7 flex max-w-full overflow-x-auto rounded-lg border border-border bg-card p-1 lg:inline-flex"
        role="tablist"
        aria-label="Profile lists"
      >
        {profileTabs.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={relationship === value}
            onClick={() => {
              setRelationship(value);
              setPage(1);
              setSelected(null);
            }}
            className={`shrink-0 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
              relationship === value
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            {label}
            <span
              className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                relationship === value ? "bg-white/20" : "bg-muted"
              }`}
            >
              {counts[value].toLocaleString()}
            </span>
          </button>
        ))}
      </div>
      <div className="relative mt-7 max-w-xl">
        <Search
          className="absolute left-3 top-3 text-muted-foreground"
          size={18}
        />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
            setSelected(null);
          }}
          placeholder={`Search ${activeTab.searchLabel}`}
          className="pl-10"
        />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_440px]">
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {profiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => setSelected(profile)}
                  className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-muted/50"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary text-primary">
                      <UserRound size={18} />
                    </span>
                    <div className="min-w-0">
                      <strong className="block truncate">
                        {profile.first_name} {profile.last_name}
                      </strong>
                      <span className="block truncate text-sm text-muted-foreground">
                        {profile.phone || profile.email}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge>{relationshipLabel[profile.relationship]}</Badge>
                    <Badge>{languageLabel(profile)}</Badge>
                  </div>
                </button>
              ))}
              {!profiles.length && (
                <p className="p-8 text-center text-muted-foreground">
                  {search
                    ? `No ${activeTab.searchLabel} match your search.`
                    : `No ${activeTab.searchLabel} yet.`}
                </p>
              )}
            </div>
            {total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4">
                <p className="text-sm text-muted-foreground">
                  {firstResult.toLocaleString()}–{lastResult.toLocaleString()}{" "}
                  of {total.toLocaleString()}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => {
                      setPage((value) => Math.max(1, value - 1));
                      setSelected(null);
                    }}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page.toLocaleString()} of {pageCount.toLocaleString()}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={page === pageCount}
                    onClick={() => {
                      setPage((value) => Math.min(pageCount, value + 1));
                      setSelected(null);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="h-fit">
          <CardContent className="p-6">
            {selected ? (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold">
                    {selected.first_name} {selected.last_name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Profile version {selected.version}
                  </p>
                </div>
                <div>
                  <Label className="mb-2 block">Relationship</Label>
                  <Select
                    value={selected.relationship}
                    onChange={(e) =>
                      void update({
                        relationship: e.target.value as Profile["relationship"],
                      })
                    }
                  >
                    <option value="neighbor">Neighbor</option>
                    <option value="volunteer">Hope Army Volunteer</option>
                    <option value="supporter">Supporter</option>
                    <option value="staff">Hope Army Team</option>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Preferred language</Label>
                  <Select
                    value={selected.preferred_locale}
                    onChange={(e) =>
                      void update({
                        preferred_locale: e.target
                          .value as Profile["preferred_locale"],
                      })
                    }
                  >
                    <option value="en-US">English (US)</option>
                    <option value="es-US">Spanish (US)</option>
                    <option value="es-ES">Spanish (Spain)</option>
                    <option value="it-IT">Italian</option>
                  </Select>
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Household size</dt>
                    <dd>{selected.household_size || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Phone</dt>
                    <dd>{selected.phone || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd>{selected.email || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">SMS consent</dt>
                    <dd>{selected.communication_consent.sms ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Email consent</dt>
                    <dd>
                      {selected.communication_consent.email ? "Yes" : "No"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Firestore mirror</dt>
                    <dd>{selected.sync?.firestore_state || "Local demo"}</dd>
                  </div>
                </dl>
                <div className="border-t pt-5">
                  <h3 className="font-bold">Service history</h3>
                  {!activity ? (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Loading activity…
                    </p>
                  ) : (
                    <>
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-secondary p-3">
                          <PackageCheck size={17} />
                          <strong className="mt-2 block text-xl">
                            {activity.summary.distribution_visits}
                          </strong>
                          <span className="text-xs text-muted-foreground">
                            distribution visits
                          </span>
                        </div>
                        <div className="rounded-lg bg-secondary p-3">
                          <UserRound size={17} />
                          <strong className="mt-2 block text-xl">
                            {activity.summary.people_served}
                          </strong>
                          <span className="text-xs text-muted-foreground">
                            people served
                          </span>
                        </div>
                        <div className="rounded-lg bg-secondary p-3">
                          <CalendarCheck2 size={17} />
                          <strong className="mt-2 block text-xl">
                            {activity.summary.event_attendances}
                          </strong>
                          <span className="text-xs text-muted-foreground">
                            events attended
                          </span>
                        </div>
                        <div className="rounded-lg bg-secondary p-3">
                          <ClipboardCheck size={17} />
                          <strong className="mt-2 block text-xl">
                            {activity.summary.form_submissions}
                          </strong>
                          <span className="text-xs text-muted-foreground">
                            forms submitted
                          </span>
                        </div>
                      </div>
                      <div className="mt-5 space-y-3">
                        {activity.items.slice(0, 12).map((item) => (
                          <div
                            key={`${item.type}:${item.id}`}
                            className="border-l-2 border-primary/20 pl-3"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <strong className="text-sm">{item.title}</strong>
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {new Date(
                                  item.occurred_at,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            {(item.description || item.status) && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {item.description}
                                {item.description && item.status ? " · " : ""}
                                {item.status}
                              </p>
                            )}
                          </div>
                        ))}
                        {!activity.items.length && (
                          <p className="text-sm text-muted-foreground">
                            No service activity recorded yet.
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select a profile to review details.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      {importOpen && (
        <Suspense fallback={null}>
          <ProfileImportDialog
            open
            onOpenChange={setImportOpen}
            onImported={() => {
              setPage(1);
              setSelected(null);
              setRefreshKey((value) => value + 1);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
